import React, { useState, useEffect } from 'react';
import {
    CircularProgress,
    Box,
    Alert,
    Tooltip,
    IconButton,
    Stack,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputLabel,
    FormControl,
} from '@mui/material';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, ExcelExport, Toolbar, Sort } from '@syncfusion/ej2-react-grids';
import Badge from '../Badge';
import { Kanban } from "react-kanban-kit";

import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const TASK_STATES = {
    Pending: 0,
    InProgress: 1,
    Completed: 2,
    Cancelled: 3,
};

const TASK_PRIORITIES = {
    Low: 0,
    Medium: 1,
    High: 2,
    Urgent: 3,
};

const PRIORITY_COLORS = [
    '#3f9948',
    '#6179f0',
    '#d2917c',
    '#b50000'
];

const STATE_COLORS = [
    '#787d7b',
    '#3159b0',
    '#3f9948',
    '#b50000'
];

const SORT_COLUMN_DICTIONARY = {
    'priority': 'priority',
    'name': 'name',
    'state': 'state',
    'assignedUserName': 'assignedUserName',
    'dueDateDate': 'dueDate',
    'createdAtDate': 'createdAt',
}

const KANBAN_DEFAULT = {
    root: {
        id: 'root',
        title: 'Root',
        children: ["cancelled", "pending", "inProgress", "completed",],
        totalChildrenCount: 4,
        padrentId: null,
    },
    cancelled: {
        id: 'cancelled',
        title: 'Cancelled',
        children: [],
        totalChildrenCount: 0,
        padrentId: 'root',
    },
    pending: {
        id: 'pending',
        title: 'Pending',
        children: [],
        totalChildrenCount: 0,
        padrentId: 'root',
    },
    inProgress: {
        id: 'inProgress',
        title: 'In Progress',
        children: [],
        totalChildrenCount: 0,
        padrentId: 'root',
    },
    completed: {
        id: 'completed',
        title: 'Completed',
        children: [],
        totalChildrenCount: 0,
        padrentId: 'root',
    },
};

export default function TaskList({ onRowClick, updateSignal, view = 'board', onChangeState }) {
    const date = new Date();
    const [gridData, setGridData] = useState({ result: [], count: 0 });
    const [kanbanData, setKanbanData] = useState(KANBAN_DEFAULT);
    const [filters, setFilters] = useState({
        priorities: Object.values(TASK_PRIORITIES),
        states: Object.values(TASK_STATES).filter(s => s !== TASK_STATES.Cancelled),
        createdAtFrom: new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0],
        createdAtTo: new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0],
        sortBy: 'createdAt',
        sortDirection: 'desc'
    });
    const [sortOptions, setSortOptions] = useState({
        columns: [{ field: 'createdAtDate', direction: 'Descending' }]
    })
    const [fetchError, setFetchError] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageSettings, setPageSettings] = useState({
        pageSize: 20,
        currentPage: 1,
        totalRecordsCount: 0
    });
    const [users, setUsers] = useState([]);

    const gridRef = React.createRef();

    const fetchUsers = async () => {
        try {
            const response = await fetch('/api/tasks/assignedusers', {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }
            const data = await response.json();
            setUsers(data);
        } catch (error) {
            console.error(error);
            setUsers([]);
        }
    }

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pageSettings.currentPage,
                pageSize: pageSettings.pageSize
            });
            if (filters.priorities?.length > 0) {
                params.append('priorities', filters.priorities.join(','));
            }
            if (filters.states?.length > 0) {
                params.append('states', filters.states.join(','));
            }
            if (filters.assignedUsers?.length > 0) {
                params.append('assignedUsers', filters.assignedUsers.join(','));
            }
            if (filters.createdAtFrom) {
                params.append('createdAtFrom', filters.createdAtFrom);
            }
            if (filters.createdAtTo) {
                params.append('createdAtTo', filters.createdAtTo);
            }
            params.append('sortBy', filters.sortBy);
            params.append('sortDirection', filters.sortDirection);

            const response = await fetch(`/api/tasks?${params.toString()}`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }
            const data = await response.json();
            setGridData({
                result: formatData(data.items),
                count: data.total
            });
            buildKanbanData(data.items);
            setFetchError('');
        } catch (error) {
            setFetchError(error.message);
        } finally {
            setLoading(false);
        }
    }
    // Defer all ResizeObserver callbacks to the next frame (prevents Chrome's RO loop warning)
    useEffect(() => {
        if (typeof window === 'undefined' || !('ResizeObserver' in window)) return;

        const OriginalRO = window.ResizeObserver;
        class PatchedRO extends OriginalRO {
            constructor(callback) {
                super((entries, observer) => {
                    // Defer to next animation frame so layout can settle
                    requestAnimationFrame(() => callback(entries, observer));
                });
            }
        }

        // Dev-only patch is fine; uncomment if you only want it in dev:
        // if (process.env.NODE_ENV !== 'production') {
        window.ResizeObserver = PatchedRO;
        // }

        // cleanup: restore original on unmount
        return () => {
            window.ResizeObserver = OriginalRO;
        };
    }, []);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const timeid = setTimeout(() => {
            fetchData();
        }, 100);

        return () => clearTimeout(timeid);
    }, [pageSettings]);

    useEffect(() => {
        if (updateSignal > 0) {
            fetchData();
        }
    }, [updateSignal]);

    const buildKanbanData = (tasks) => {
        // IMPORTANT: clone the template; mutating the shared KANBAN_DEFAULT causes repeated pushes across fetches
        const newKanbanData = structuredClone
            ? structuredClone(KANBAN_DEFAULT)
            : JSON.parse(JSON.stringify(KANBAN_DEFAULT));

        tasks.forEach(task => {
            const card = {
                id: task.id,
                title: task.name,
                parentId: Object.keys(TASK_STATES)[task.state].replace(/^./, c => c.toLowerCase()),
                content: {
                    description: task.description,
                    priority: Object.keys(TASK_PRIORITIES)[task.priority],
                    assignedUserName: task.assignedUserName,
                    dueDate: task.dueDate,
                    createdAt: task.createdAt,
                    state: Object.keys(TASK_STATES)[task.state],
                }
            };
            newKanbanData[card.id] = card;
            switch (task.state) {
                case TASK_STATES.Pending:
                    newKanbanData.pending.children.push(card.id);
                    newKanbanData.pending.totalChildrenCount += 1;
                    break;
                case TASK_STATES.InProgress:
                    newKanbanData.inProgress.children.push(card.id);
                    newKanbanData.inProgress.totalChildrenCount += 1;
                    break;
                case TASK_STATES.Completed:
                    newKanbanData.completed.children.push(card.id);
                    newKanbanData.completed.totalChildrenCount += 1;
                    break;
                case TASK_STATES.Cancelled:
                    newKanbanData.cancelled.children.push(card.id);
                    newKanbanData.cancelled.totalChildrenCount += 1;
                    break;
            }
        });

        // Defer the state write to the next frame
        requestAnimationFrame(() => setKanbanData(newKanbanData));
    };

    const toolbarClick = async (args) => {
        if (gridRef.current && args.item.id === 'task-grid_excelexport') {
            try {
                const params = new URLSearchParams();
                if (filters.priorities?.length > 0) {
                    params.append('priorities', filters.priorities.join(','));
                }
                if (filters.states?.length > 0) {
                    params.append('states', filters.states?.join(','));
                }
                if (filters.assignedUsers?.length > 0) {
                    params.append('assignedUsers', filters.assignedUsers.join(','));
                }
                if (filters.createdAtFrom) {
                    params.append('createdAtFrom', filters.createdAtFrom);
                }
                if (filters.createdAtTo) {
                    params.append('createdAtTo', filters.createdAtTo);
                }
                params.append('sortBy', filters.sortBy);
                params.append('sortDirection', filters.sortDirection);
                params.append('export', true);
                let url = `/api/tasks?${params.toString()}`;
                url += '&' + new URLSearchParams(filters).toString();
                const response = await fetch(url);
                const result = await response.json();
                gridRef.current.showSpinner();
                gridRef.current.excelExport({
                    exportType: 'AllPages',
                    dataSource: result.map(t => ({
                        id: t.id,
                        name: t.name,
                        description: t.description,
                        assignedUserId: t.assignedUser,
                        assignedUserName: t.assignedUserName,
                        dueDateUTC: t.dueDate ? new Date(t.dueDate).toLocaleString('en-US') : null,
                        dueDateLocale: t.dueDate ? new Date(t.dueDate + 'Z').toLocaleString('en-US') : null,
                        priority: Object.keys(TASK_PRIORITIES)[t.priority],
                        state: Object.keys(TASK_STATES)[t.state],
                        systemTask: t.systemTask,
                        completedAtUTC: t.completedAt ? new Date(t.completedAt).toLocaleString('en-US') : null,
                        completedAtLocale: t.completedAt ? new Date(t.completedAt + 'Z').toLocaleString('en-US') : null,
                        createdAtUTC: new Date(t.createdAt).toLocaleString('en-US'),
                        createdAtLocale: new Date(t.createdAt + 'Z').toLocaleString('en-US'),
                        createdBy: t.createdBy,
                        updatedAtUTC: new Date(t.updatedAt).toLocaleString('en-US'),
                        updatedAtLocale: new Date(t.updatedAt + 'Z').toLocaleString('en-US'),
                        updatedBy: t.updatedBy,
                    })),
                    columns: [
                        { field: 'id', headerText: 'Id' },
                        { field: 'name', headerText: 'Name' },
                        { field: 'description', headerText: 'Description' },
                        { field: 'assignedUserId', headerText: 'Assigned User Id' },
                        { field: 'assignedUserName', headerText: 'Assigned To' },
                        { field: 'dueDateUTC', headerText: 'Due Date (UTC)' },
                        { field: 'dueDateLocale', headerText: 'Due Date' },
                        { field: 'priority', headerText: 'Priority' },
                        { field: 'state', headerText: 'State' },
                        { field: 'systemTask', headerText: 'System Task' },
                        { field: 'completedAtUTC', headerText: 'Completed At (UTC)' },
                        { field: 'completedAtLocale', headerText: 'Completed At' },
                        { field: 'createdAtUTC', headerText: 'Created At (UTC)' },
                        { field: 'createdAtLocale', headerText: 'Created At' },
                        { field: 'createdBy', headerText: 'Created By' },
                        { field: 'updatedAtUTC', headerText: 'Updated At (UTC)' },
                        { field: 'updatedAtLocale', headerText: 'Updated At' },
                        { field: 'updatedBy', headerText: 'Updated By' },
                    ]
                });
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                gridRef.current.hideSpinner();
            }
        }
    }
    const excelExportComplete = () => gridRef.current.hideSpinner();

    const handleActionBegin = (args) => {
        if (args.requestType === 'paging') {
            setPageSettings(prev => ({
                ...prev,
                currentPage: args.currentPage
            }));
        }
        if (args.requestType === 'sorting') {
            setSortOptions({
                columns: [{ field: args.columnName ?? 'createdAtDate', direction: args.direction ?? 'Descending' }]
            })
            const newFilter = {
                ...filters,
                sortBy: SORT_COLUMN_DICTIONARY[args.columnName] ?? 'createdAt',
                sortDirection: args.direction
                    ? (args.direction === 'Ascending' ? 'asc' : 'desc')
                    : 'desc'
            };
            setFilters(newFilter)
            setPageSettings(prev => ({ ...prev, currentPage: 1 }));
        }
    };

    const dataStateChange = () => document.querySelector('.e-spinner-pane').style.display = "none";

    const formatData = (data) => {
        return data.map(item => ({
            ...item,
            createdAtDate: <span>{formatDate(item.createdAt)} <b>{formatTime(item.createdAt)}</b></span>,
            completedAtDate: item.completedAt ? <span>{formatDate(item.completedAt)} <b>{formatTime(item.completedAt)}</b></span> : null,
            dueDateDate: item.dueDate ? <b>{formatDate(item.dueDate)}</b> : null,
        }));
    };

    const formatDate = (dateString) => {
        const date = new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = date.getDate().toString().padStart(2, '0');
        const yyyymmdd = `${year}-${month}-${day}`;
        return yyyymmdd;
    }

    const formatTime = (dateString) => {
        const date = new Date(new Date(dateString + 'Z').toLocaleString('en-US'));
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const hhmm = `${hours}:${minutes}:${seconds}`;
        return hhmm;
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const STATE_OPTIONS = Object.keys(TASK_STATES).map((state, index) => ({ label: state, value: index }));

    const PRIORITY_OPTIONS = Object.keys(TASK_PRIORITIES).map((priority, index) => ({ label: priority, value: index }));

    const USERS_OPTIONS = users.map(user => ({ label: user.userName, value: user.id }));

    const handleFilterStateChange = (event) => {
        const value = event.target.value;
        setFilters(prev => ({
            ...prev,
            states: value
        }));
        setPageSettings(prev => ({ ...prev, currentPage: 1 }));
    }

    const handleFilterPriorityChange = (event) => {
        const value = event.target.value;
        setFilters(prev => ({
            ...prev,
            priorities: value
        }));
        setPageSettings(prev => ({ ...prev, currentPage: 1 }));
    }

    const handleFilterUserChange = (event) => {
        const value = event.target.value;
        setFilters(prev => ({
            ...prev,
            assignedUsers: value
        }));
        setPageSettings(prev => ({ ...prev, currentPage: 1 }));
    }

    return (
        <>
            <Box sx={{ my: 2 }}>
                <Stack direction="row" spacing={1}>
                    <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="task-states-select">State</InputLabel>
                        <Select
                            labelId="task-states-select"
                            label="States"
                            onChange={handleFilterStateChange}
                            size="small"
                            multiple
                            MenuProps={MenuProps}
                            value={filters.states || []}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <em>States</em>;
                                }
                                return selected.map(s => STATE_OPTIONS[s].label).join(', ')
                            }}
                        >
                            <MenuItem disabled value="">
                                <em>States</em>
                            </MenuItem>
                            {STATE_OPTIONS.map((state) => (
                                <MenuItem key={state.value} value={state.value}>
                                    <Checkbox checked={filters.states?.includes(state.value)} />
                                    <ListItemText primary={state.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="task-priorities-select">State</InputLabel>
                        <Select
                            labelId="task-priorities-select"
                            label="Priorities"
                            onChange={handleFilterPriorityChange}
                            size="small"
                            multiple
                            MenuProps={MenuProps}
                            value={filters.priorities || []}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <em>Priorities</em>;
                                }
                                return selected.map(s => PRIORITY_OPTIONS[s].label).join(', ')
                            }}
                        >
                            <MenuItem disabled value="">
                                <em>Priorities</em>
                            </MenuItem>
                            {PRIORITY_OPTIONS.map((state) => (
                                <MenuItem key={state.value} value={state.value}>
                                    <Checkbox checked={filters.priorities.includes(state.value)} />
                                    <ListItemText primary={state.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, width: 300 }}>
                        <InputLabel id="task-users-select">Assigned Users</InputLabel>
                        <Select
                            labelId="task-users-select"
                            label="Assigned Users"
                            onChange={handleFilterUserChange}
                            size="small"
                            multiple
                            MenuProps={MenuProps}
                            value={filters.assignedUsers || []}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <em>Assigned Users</em>;
                                }
                                return selected.map(s => {
                                    const user = USERS_OPTIONS.find(u => u.value === s);
                                    return user ? user.label : s;
                                }).join(', ')
                            }}
                        >
                            <MenuItem disabled value="">
                                <em>Assigned Users</em>
                            </MenuItem>
                            {USERS_OPTIONS.map((user) => (
                                <MenuItem key={user.value} value={user.value}>
                                    <Checkbox checked={filters.assignedUsers ? filters.assignedUsers.includes(user.value) : false} />
                                    <ListItemText primary={user.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <span style={{ width: '300px' }}>(Created at) From:</span>
                    <span style={{ width: '300px' }}>(Created at) To:</span>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <FormControl>
                        <div className='MuiFormControl-root MuiTextField-root css-1xp5r68-MuiFormControl-root-MuiTextField-root'>
                            <div
                                className='MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl css-63ss9t-MuiInputBase-root-MuiOutlinedInput-root'
                                style={{
                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                    borderRadius: '4px',
                                    height: '40px',
                                    width: '300px'
                                }}>
                                <input
                                    type='date'
                                    className='MuiInputBase-input MuiOutlinedInput-input css-16wblaj-MuiInputBase-input-MuiOutlinedInput-input '
                                    value={filters.createdAtFrom ?? ''}
                                    style={{
                                        border: 'none',
                                        height: '98%',
                                        width: '90%'
                                    }}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setFilters(prev => ({
                                            ...prev,
                                            createdAtFrom: value
                                        }));
                                        setPageSettings(prev => ({ ...prev, currentPage: 1 }));
                                    }}
                                />
                            </div>
                        </div>
                    </FormControl>
                    <FormControl>
                        <div className='MuiFormControl-root MuiTextField-root css-1xp5r68-MuiFormControl-root-MuiTextField-root'>
                            <div
                                className='MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl css-63ss9t-MuiInputBase-root-MuiOutlinedInput-root'
                                style={{
                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                    borderRadius: '4px',
                                    height: '40px',
                                    width: '300px'
                                }}>
                                <input
                                    type='date'
                                    className='MuiInputBase-input MuiOutlinedInput-input css-16wblaj-MuiInputBase-input-MuiOutlinedInput-input '
                                    value={filters.createdAtTo ?? ''}
                                    style={{
                                        border: 'none',
                                        height: '98%',
                                        width: '90%'
                                    }}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setFilters(prev => ({
                                            ...prev,
                                            createdAtTo: value
                                        }));
                                        setPageSettings(prev => ({ ...prev, currentPage: 1 }));
                                    }}
                                />
                            </div>
                        </div>
                    </FormControl>
                </Stack>
            </Box>

            {
                loading && (view == 'list' || updateSignal == 0)
                    ? <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                    : (
                        <>
                            {fetchError != ''
                                ? <Alert severity="error">{fetchError}</Alert>
                                : (
                                    view === 'board' ? (
                                        <Kanban
                                            dataSource={kanbanData}
                                            virtualization={false}
                                            onCardMove={(move) => {
                                                onChangeState(TASK_STATES[move.toColumnId.substring(0, 1).toUpperCase() + move.toColumnId.substring(1)], move.cardId);
                                            }}
                                            onCardClick={(card) => {
                                                var target = card.target;
                                                var id = target?.parentElement?.parentElement?.getAttribute('data-test-id') ?? null;
                                                if (id) {
                                                    onRowClick(id);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <GridComponent
                                            id='task-grid'
                                            ref={gridRef}
                                            dataSource={gridData}
                                            pageSettings={pageSettings}
                                            actionBegin={handleActionBegin}
                                            height='100%'
                                            toolbar={['ExcelExport']}
                                            toolbarClick={toolbarClick}
                                            excelExportComplete={excelExportComplete}
                                            dataStateChange={dataStateChange}
                                            sortSettings={sortOptions}
                                            allowPaging
                                            allowExcelExport
                                            allowSorting
                                        >
                                            <ColumnsDirective>
                                                <ColumnDirective field='priority' headerText='' width={50} template={(props) => (
                                                    <Tooltip title={Object.keys(TASK_PRIORITIES)[props.priority]} placement="top">
                                                        <Box sx={{
                                                            color: 'white',
                                                            backgroundColor: PRIORITY_COLORS[props.priority],
                                                            borderRadius: '100px',
                                                            padding: '2px 8px',
                                                            textAlign: 'center',
                                                            display: 'inline-block',
                                                            width: '15px',
                                                            height: '15px',
                                                        }}>

                                                        </Box>
                                                    </Tooltip>
                                                )} />
                                                <ColumnDirective field='name' headerText='Name' />
                                                <ColumnDirective field='state' headerText='State' width={100} template={(props) => (
                                                    <Box
                                                        sx={{
                                                            color: 'white',
                                                            backgroundColor: STATE_COLORS[props.state],
                                                            borderRadius: '100px',
                                                            padding: '2px 8px',
                                                            textAlign: 'center',
                                                            display: 'inline-block'
                                                        }}>
                                                        {Object.keys(TASK_STATES)[props.state]}
                                                    </Box>
                                                )} />
                                                <ColumnDirective field='completedAtDate' type='date' format='yMd' headerText='Completed At' template={(props) => (
                                                    <>
                                                        {
                                                            props.state === TASK_STATES.Completed
                                                            && props.completedAtDate
                                                            && <Badge type='default' icon={<CalendarMonthIcon sx={{ fontSize: 12 }} />}>{props.completedAtDate}</Badge>
                                                        }
                                                    </>
                                                )} />
                                                <ColumnDirective field='assignedUserName' headerText='Assigned To' />
                                                <ColumnDirective field='dueDateDate' type='date' format='yMd' headerText='Due Date' template={(props) => (
                                                    <>
                                                        {props.dueDate && <Badge type='default' icon={<CalendarMonthIcon sx={{ fontSize: 12 }} />}>{props.dueDateDate}</Badge>}
                                                    </>
                                                )} />
                                                <ColumnDirective field='createdAtDate' type='date' format='yMd' headerText='Created At' template={(props) => (
                                                    <Badge type='default' icon={<CalendarMonthIcon sx={{ fontSize: 12 }} />}>{props.createdAtDate}</Badge>
                                                )} />
                                                <ColumnDirective headerText='View' headerTemplate={() => <VisibilityIcon fontSize='small' />} width={80} template={(props) => (
                                                    <IconButton size='small' onClick={() => onRowClick(props.id)}>
                                                        <VisibilityIcon fontSize='small' />
                                                    </IconButton>
                                                )} />
                                            </ColumnsDirective>
                                            <Inject services={[Page, ExcelExport, Toolbar, Sort]} />
                                        </GridComponent>
                                    )
                                )
                            }
                        </>
                    )
            }
        </>
    );
}
